/** The roof reconstruction report of a design: a full-image viewer with zoom and pan, and a download.
 *
 * Images come from short-lived signed URLs (`/designs/{id}/report/link`). The viewer opens on the 1600 px WebP
 * `preview` and loads the full-size WebP `view` only once zoomed past FULL_AT_ZOOM (lib/zoomView.ts); Download
 * fetches the PNG (a fresh link per click, so an old tab never offers an expired one). Click or scroll zooms (around
 * the pointer), drag pans (two fingers pinch), + / − / 0 and the arrow keys do the same from the keyboard. Esc or the
 * backdrop closes it; focus stays inside while it is open and returns to the button that opened it. Full screen on
 * phones.
 *
 * Performance: zoom and pan never render React. Wheel and pointer handlers only update a ref and schedule one
 * requestAnimationFrame, which writes a single `translate3d(...) scale(...)` on the image layer; `will-change` is
 * set only while interacting (so the layer is re-rasterised sharp at the final scale afterwards), images are
 * decoded (`img.decode()`) before they are shown, and nothing behind the stage is blurred. React state changes only
 * when something visible in the toolbar flips (zoomed, at the limit) or the full-size image is wanted. */
import { useQuery } from "@tanstack/react-query";
import { Download, FileImage, Maximize2, X, ZoomIn, ZoomOut } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api, errorMessage, unwrap } from "../api/client";
import { shownVersion } from "../lib/format";
import { FIT, MAX_ZOOM, clampView, fitSize, layerTransform, needsFull, zoomAt, type Size, type View } from "../lib/zoomView";
import { Alert, Button, useToast } from "./ui";
import { useT, useIntlShape } from "../i18n/I18nProvider";

const CLICK_ZOOM = 2.5;
const STEP_ZOOM = 1.4;
const SETTLE_MS = 160; // after the last wheel or pointer event: drop will-change and let the layer re-raster sharp
const ANIMATE_MS = 180; // buttons, keys and clicks glide; wheel and drag follow the pointer directly

function useFocusTrap(ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const node = ref.current;
    node?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !node) return;
      const items = Array.from(
        node.querySelectorAll<HTMLElement>('button:not([disabled]), [href], [tabindex]:not([tabindex="-1"])'),
      ).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (!node.contains(document.activeElement)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      opener?.focus?.();
    };
  }, [ref]);
}

function reportLink(designId: string, variant: "png" | "view" | "preview") {
  return unwrap(api.GET("/v1/designs/{design_id}/report/link", { params: { path: { design_id: designId }, query: { variant } } }));
}

/** Shows an <img> only once it is decoded, so the first frame with it never waits on a 2880 px decode. */
function decodeThen(img: HTMLImageElement, done: () => void) {
  (img.decode ? img.decode() : Promise.resolve()).catch(() => undefined).then(done);
}

export function ReportModal({ designId, title, versionOffset, onClose }: { designId: string; title: string; versionOffset?: number | null; onClose: () => void }) {
  const dialog = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const zoomLabelRef = useRef<HTMLSpanElement>(null);
  const toast = useToast();
  const t = useT();
  const intl = useIntlShape();
  // the zoom label is written outside React (paint): it reads the formatter through a ref
  const percent = useRef((n: number) => intl.formatNumber(n, { style: "percent" }));
  percent.current = (n: number) => intl.formatNumber(n, { style: "percent" });
  useFocusTrap(dialog);

  // The link lives five minutes: never reuse a cached one for a new opening.
  const preview = useQuery({ queryKey: ["report-link", designId, "preview"], gcTime: 0, staleTime: 0, retry: 1, queryFn: () => reportLink(designId, "preview") });
  const [wantFull, setWantFull] = useState(false);
  const [previewIsFull, setPreviewIsFull] = useState(false); // a report drawn before previews existed
  const full = useQuery({
    queryKey: ["report-link", designId, "view"],
    gcTime: 0,
    staleTime: 0,
    retry: 1,
    enabled: wantFull && !previewIsFull,
    queryFn: () => reportLink(designId, "view"),
  });
  const [loaded, setLoaded] = useState(false);
  const [fullReady, setFullReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [flags, setFlags] = useState({ zoomed: false, atMax: false });

  // Everything the interaction touches lives in refs: no React render per wheel or pointer event.
  const view = useRef<View>(FIT);
  const stage = useRef<Size>({ w: 0, h: 0 });
  const natural = useRef<Size>({ w: 2880, h: 1800 });
  const fitted = useRef<Size>({ w: 0, h: 0 });
  const previewWidth = useRef(1600);
  const loadedRef = useRef(false);
  const flagsRef = useRef(flags);
  const wantFullRef = useRef(false);
  const frame = useRef(0);
  const settleTimer = useRef(0);
  const animateTimer = useRef(0);

  /** Writes the current view to the DOM: one transform on the layer, the zoom label, rare React flags. */
  const paint = useCallback(() => {
    frame.current = 0;
    const layer = layerRef.current;
    const v = view.current;
    if (layer) layer.style.transform = layerTransform(v, stage.current, fitted.current);
    const fit = fitted.current.w / natural.current.w || 0;
    const label = percent.current(Math.round(v.scale * fit * 100) / 100 || 0);
    // Only when it changes: rewriting the same text still relayouts the toolbar.
    if (zoomLabelRef.current && zoomLabelRef.current.textContent !== label) zoomLabelRef.current.textContent = label;
    const next = { zoomed: v.scale > 1.01, atMax: v.scale >= MAX_ZOOM - 1e-6 };
    if (next.zoomed !== flagsRef.current.zoomed || next.atMax !== flagsRef.current.atMax) {
      flagsRef.current = next;
      setFlags(next);
    }
    if (!wantFullRef.current && loadedRef.current && needsFull(v.scale, fitted.current.w, window.devicePixelRatio || 1, previewWidth.current)) {
      wantFullRef.current = true;
      setWantFull(true);
    }
  }, []);

  const schedule = useCallback(() => {
    if (!frame.current) frame.current = requestAnimationFrame(paint);
  }, [paint]);

  /** Marks the layer as moving (will-change) until SETTLE_MS after the last call. */
  const interacting = useCallback((animate = false) => {
    const node = stageRef.current;
    if (!node) return;
    node.classList.add("interacting");
    window.clearTimeout(animateTimer.current);
    if (animate) {
      node.classList.add("animating");
      animateTimer.current = window.setTimeout(() => node.classList.remove("animating"), ANIMATE_MS);
    } else node.classList.remove("animating");
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => node.classList.remove("interacting"), animate ? ANIMATE_MS + SETTLE_MS : SETTLE_MS);
  }, []);

  const setView = useCallback(
    (next: View, animate = false) => {
      view.current = clampView(next, stage.current, fitted.current);
      interacting(animate);
      schedule();
    },
    [interacting, schedule],
  );

  /** Re-measures the stage and resizes the layer (opening, window resize, the image's real size). */
  const layout = useCallback(() => {
    const node = stageRef.current;
    if (!node) return;
    stage.current = { w: node.clientWidth, h: node.clientHeight };
    fitted.current = fitSize(stage.current, natural.current);
    const layer = layerRef.current;
    if (layer) {
      layer.style.width = `${fitted.current.w}px`;
      layer.style.height = `${fitted.current.h}px`;
    }
    view.current = clampView(view.current, stage.current, fitted.current);
    if (frame.current) cancelAnimationFrame(frame.current);
    paint();
  }, [paint]);

  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    layout();
    const observer = new ResizeObserver(layout);
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame.current);
      window.clearTimeout(settleTimer.current);
      window.clearTimeout(animateTimer.current);
    };
  }, [layout]);

  // The report's full size (whatever variant the link is for) sets the aspect.
  useEffect(() => {
    const d = preview.data;
    if (d?.width && d?.height && (d.width !== natural.current.w || d.height !== natural.current.h)) {
      natural.current = { w: d.width, h: d.height };
      layout();
    }
  }, [preview.data, layout]);

  const centreOffset = (clientX: number, clientY: number) => {
    const r = stageRef.current!.getBoundingClientRect();
    return [clientX - r.left - r.width / 2, clientY - r.top - r.height / 2] as const;
  };

  const zoomBy = useCallback(
    (factor: number, px = 0, py = 0, animate = true) => {
      const v = view.current;
      setView(zoomAt(v, v.scale * factor, px, py, stage.current, fitted.current), animate);
    },
    [setView],
  );

  // Wheel, drag and pinch: native listeners, nothing but arithmetic and one scheduled frame per display refresh.
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const pointers = new Map<number, { x: number; y: number }>();
    let drag: { x: number; y: number; vx: number; vy: number; moved: boolean } | null = null;
    let pinch: { dist: number; view: View } | null = null;

    const onWheel = (e: WheelEvent) => {
      if (!loadedRef.current) return;
      e.preventDefault(); // non-passive on purpose: the wheel zooms the image, never the page
      const [px, py] = centreOffset(e.clientX, e.clientY);
      const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const v = view.current;
      setView(zoomAt(v, v.scale * Math.exp(-delta * 0.0015), px, py, stage.current, fitted.current));
    };
    const pinchState = () => {
      const [a, b] = Array.from(pointers.values());
      return { dist: Math.hypot(a.x - b.x, a.y - b.y) || 1, mid: centreOffset((a.x + b.x) / 2, (a.y + b.y) / 2) };
    };
    const onDown = (e: PointerEvent) => {
      if (!loadedRef.current || (e.pointerType === "mouse" && e.button !== 0)) return;
      node.setPointerCapture(e.pointerId);
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const v = view.current;
      if (pointers.size === 2) {
        pinch = { dist: pinchState().dist, view: v };
        drag = null;
      } else if (pointers.size === 1) {
        drag = { x: e.clientX, y: e.clientY, vx: v.x, vy: v.y, moved: false };
      }
    };
    const onMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pinch && pointers.size === 2) {
        const { dist, mid } = pinchState();
        setView(zoomAt(pinch.view, (pinch.view.scale * dist) / pinch.dist, mid[0], mid[1], stage.current, fitted.current));
        return;
      }
      if (!drag) return;
      const dx = e.clientX - drag.x;
      const dy = e.clientY - drag.y;
      if (!drag.moved && Math.abs(dx) + Math.abs(dy) > 4) drag.moved = true;
      if (drag.moved) setView({ scale: view.current.scale, x: drag.vx + dx, y: drag.vy + dy });
    };
    const onUp = (e: PointerEvent) => {
      if (!pointers.delete(e.pointerId)) return;
      const d = drag;
      if (pinch) {
        // Lifting one finger of a pinch: carry on panning with the other, never treat it as a click.
        pinch = null;
        const rest = Array.from(pointers.values())[0];
        drag = rest ? { x: rest.x, y: rest.y, vx: view.current.x, vy: view.current.y, moved: true } : null;
        return;
      }
      drag = null;
      if (!d || d.moved || e.type === "pointercancel") return;
      // A click: zoom in there, or back to fit when zoomed in.
      const [px, py] = centreOffset(e.clientX, e.clientY);
      if (view.current.scale > 1.05) setView(FIT, true);
      else setView(zoomAt(view.current, CLICK_ZOOM, px, py, stage.current, fitted.current), true);
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    node.addEventListener("pointerdown", onDown);
    node.addEventListener("pointermove", onMove, { passive: true });
    node.addEventListener("pointerup", onUp, { passive: true });
    node.addEventListener("pointercancel", onUp, { passive: true });
    return () => {
      node.removeEventListener("wheel", onWheel);
      node.removeEventListener("pointerdown", onDown);
      node.removeEventListener("pointermove", onMove);
      node.removeEventListener("pointerup", onUp);
      node.removeEventListener("pointercancel", onUp);
    };
  }, [setView]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        onCloseRef.current();
        return;
      }
      if (!loadedRef.current || (e.target as HTMLElement)?.tagName === "INPUT") return;
      const step = 80;
      const v = view.current;
      if (e.key === "+" || e.key === "=") zoomBy(STEP_ZOOM);
      else if (e.key === "-" || e.key === "_") zoomBy(1 / STEP_ZOOM);
      else if (e.key === "0") setView(FIT, true);
      else if (e.key === "ArrowLeft") setView({ ...v, x: v.x + step }, true);
      else if (e.key === "ArrowRight") setView({ ...v, x: v.x - step }, true);
      else if (e.key === "ArrowUp") setView({ ...v, y: v.y + step }, true);
      else if (e.key === "ArrowDown") setView({ ...v, y: v.y - step }, true);
      else return;
      e.preventDefault();
    };
    // Capture: the backdrop stops React keydown events from bubbling, which also stops them reaching window, so with
    // focus inside the dialog (the close button has it on opening) a bubbling listener would never hear Esc.
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [zoomBy, setView]);

  const download = async () => {
    setDownloading(true);
    try {
      const png = await reportLink(designId, "png");
      const a = document.createElement("a");
      a.href = png.url;
      a.download = png.filename; // same-origin links; signed bucket links carry Content-Disposition: attachment
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      toast(errorMessage(e), true);
    } finally {
      setDownloading(false);
    }
  };

  const { zoomed, atMax } = flags;
  const error = preview.error;
  // A portal (the opener may sit in a table row); React events still bubble to the opener's ancestors, so stop them.
  return createPortal(
    <div
      className="modal-backdrop report-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <div ref={dialog} className="report-modal" role="dialog" aria-modal="true" aria-labelledby="report-title">
        <header className="report-head">
          <div className="report-title">
            <FileImage size={18} aria-hidden />
            <div style={{ minWidth: 0 }}>
              <h2 id="report-title">{t("designs.report.title")}</h2>
              <p className="ellipsis">
                {preview.data ? t("designs.report.subtitle", { title, version: shownVersion(preview.data.version_number, versionOffset) }) : title}
              </p>
            </div>
          </div>
          <div className="report-tools" role="toolbar" aria-label={t("designs.report.toolbar")}>
            <Button size="sm" variant="ghost" icon={<ZoomOut />} aria-label={t("designs.report.zoomOut")} disabled={!loaded || !zoomed} onClick={() => zoomBy(1 / STEP_ZOOM)} />
            <span className="report-zoom" ref={zoomLabelRef} aria-live="polite">
              {intl.formatNumber(0, { style: "percent" })}
            </span>
            <Button size="sm" variant="ghost" icon={<ZoomIn />} aria-label={t("designs.report.zoomIn")} disabled={!loaded || atMax} onClick={() => zoomBy(STEP_ZOOM)} />
            <Button size="sm" variant="ghost" icon={<Maximize2 />} aria-label={t("designs.report.fit")} disabled={!loaded || !zoomed} onClick={() => setView(FIT, true)} />
            <Button size="sm" variant="primary" icon={<Download />} loading={downloading} disabled={!preview.data} onClick={download}>
              <span className="hide-xs">{t("common.download")}</span>
            </Button>
            <button className="icon-btn report-close" onClick={onClose} aria-label={t("common.close")} data-autofocus>
              <X />
            </button>
          </div>
        </header>
        <div ref={stageRef} className={`report-stage${zoomed ? " zoomed" : ""}${loaded ? " ready" : ""}`}>
          {error || failed ? (
            <div className="report-message">
              <Alert tone="danger">{error ? errorMessage(error) : t("designs.report.loadFailed")}</Alert>
            </div>
          ) : null}
          {!loaded && !error && !failed && (
            <div className="report-message" role="status">
              <span className="spinner" /> <span>{t("designs.report.loading")}</span>
            </div>
          )}
          <div ref={layerRef} className={`report-layer${fullReady ? " full-ready" : ""}`} style={{ visibility: loaded ? "visible" : "hidden" }}>
            {preview.data && !failed && (
              <img
                src={preview.data.url}
                alt={t("designs.report.alt", { title })}
                draggable={false}
                decoding="async"
                className="report-preview"
                onLoad={(e) => {
                  const img = e.currentTarget;
                  previewWidth.current = img.naturalWidth || 1600;
                  if (img.naturalWidth >= natural.current.w) setPreviewIsFull(true);
                  if (!preview.data?.width && img.naturalWidth && img.naturalHeight) {
                    natural.current = { w: img.naturalWidth, h: img.naturalHeight };
                    layout();
                  }
                  decodeThen(img, () => {
                    loadedRef.current = true;
                    setLoaded(true);
                  });
                }}
                onError={() => setFailed(true)}
              />
            )}
            {/* The preview stays on screen if the full-size image cannot be loaded. */}
            {full.data && !previewIsFull && (
              <img
                src={full.data.url}
                alt=""
                aria-hidden
                draggable={false}
                decoding="async"
                className="report-full"
                onLoad={(e) => decodeThen(e.currentTarget, () => setFullReady(true))}
              />
            )}
          </div>
        </div>
        <footer className="report-foot">
          <span className="faint small">{t("designs.report.help")}</span>
          <Button size="sm" onClick={onClose}>
            {t("common.close")}
          </Button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

/** "See report" for a design whose roof report is ready (renders nothing otherwise). */
export function SeeReportButton({ designId, title, available, versionOffset, size = "sm", label }: {
  designId: string;
  title: string;
  /** The design's `version_offset`, so the viewer numbers the version as the design page does. */
  versionOffset?: number | null;
  available: boolean | undefined;
  size?: "sm" | "lg";
  label?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  if (!available) return null;
  return (
    <>
      <Button
        size={size === "lg" ? undefined : size}
        icon={<FileImage />}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        aria-haspopup="dialog"
      >
        {label ?? t("designs.report.see")}
      </Button>
      {open && <ReportModal designId={designId} title={title} versionOffset={versionOffset} onClose={() => setOpen(false)} />}
    </>
  );
}
